import type { BomVersion, TechnicalPassport } from './types'

export type PassportConsistencySeverity = 'error' | 'warning'

export interface PassportConsistencyIssue {
  code:
    | 'bom_missing'
    | 'passport_route_missing'
    | 'passport_material_missing'
    | 'bom_material_missing'
    | 'quantity_mismatch'
  severity: PassportConsistencySeverity
  message: string
}

export interface PassportConsistencyResult {
  isConsistent: boolean
  issues: PassportConsistencyIssue[]
  matchedOperations: number
  matchedMaterials: number
}

function normalized(value: string | null | undefined): string {
  return (value ?? '').trim().toLocaleLowerCase()
}

function nearlyEqual(left: number, right: number): boolean {
  return Math.abs(left - right) <= Math.max(0.000001, Math.abs(left) * 0.01)
}

export function diagnosePassportConsistency(
  bom: BomVersion | null,
  passport: TechnicalPassport,
): PassportConsistencyResult {
  if (!bom) {
    return {
      isConsistent: false,
      matchedOperations: 0,
      matchedMaterials: 0,
      issues: [{
        code: 'bom_missing',
        severity: 'error',
        message: 'No active BOM version exists for this product.',
      }],
    }
  }

  const issues: PassportConsistencyIssue[] = []
  const routes = passport.routes ?? []
  const passportMaterials = passport.raw_materials ?? []
  let matchedOperations = 0
  let matchedMaterials = 0

  for (const operation of bom.operations) {
    const matchingRoute = routes.find((route) =>
      (operation.operation_reference_id !== null &&
        route.operation_id === operation.operation_reference_id) ||
      normalized(route.operation_name) === normalized(operation.operation_name),
    )
    if (matchingRoute) {
      matchedOperations += 1
    } else {
      issues.push({
        code: 'passport_route_missing',
        severity: 'error',
        message: `Passport route is missing BOM operation #${operation.operation_sequence} ${operation.operation_name}.`,
      })
    }

    for (const material of operation.materials) {
      const matchingMaterial = passportMaterials.find(
        (row) => row.material_product_id === material.material_id,
      )
      if (!matchingMaterial) {
        issues.push({
          code: 'passport_material_missing',
          severity: 'error',
          message: `Passport materials are missing ${material.material_name} (${material.material_sku}).`,
        })
        continue
      }
      matchedMaterials += 1
      if (
        matchingMaterial.consumption_per_pair !== undefined &&
        !nearlyEqual(material.quantity, matchingMaterial.consumption_per_pair)
      ) {
        issues.push({
          code: 'quantity_mismatch',
          severity: 'warning',
          message: `${material.material_name} quantity differs: BOM ${material.quantity} ${material.material_unit}, passport ${matchingMaterial.consumption_per_pair} ${matchingMaterial.uom ?? material.material_unit}.`,
        })
      }
    }
  }

  const bomMaterialIds = new Set(
    bom.operations.flatMap((operation) =>
      operation.materials.map((material) => material.material_id),
    ),
  )
  for (const material of passportMaterials) {
    if (!bomMaterialIds.has(material.material_product_id ?? 0)) {
      issues.push({
        code: 'bom_material_missing',
        severity: 'warning',
        message: `${material.material_name_snapshot ?? `Material #${material.material_product_id}`} exists in the passport but not in the active BOM.`,
      })
    }
  }

  return {
    isConsistent: issues.length === 0,
    issues,
    matchedOperations,
    matchedMaterials,
  }
}
